import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Divider,
  Select,
  FormControl,
  Alert,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../../hooks/useUser';
import { useDesignatedOperators } from '../hooks/useDesignatedOperators';
import { 
  UserRoles, 
  UserRoleLabels,
  AccountTypes, 
  AccountTypeLabels, 
  PPOValues, 
  ModuleNames, 
  ModuleUrls, 
  ModuleToggleKeys
} from '../../../common/constants/userConstants';
import { ModuleIds, PermissionIds } from '../../../common/constants/moduleIds';
import {
  themeColors,
  SegmentedControlContainer,
  SegmentButton,
  ModuleToggleContainer,
  ModuleToggleLabel,
  SelectAllButton,
  ModuleSection,
  ModuleSectionHeader,
  ModuleSectionTitle,
  CheckboxList,
  FormContainer,
  FormSection,
  LeftColumn,
  RightColumn,
  FieldLabel,
  HelperText,
  PermissionsContainer
} from './EditUser.styled';
import CustomSwitch from '../../../common/components/CustomSwitch';
import CustomCheckbox from '../../../common/components/CustomCheckbox';
import { sanitizeTextInput, hasDangerousInput, isValidSamId, getInputErrorMessage } from '../../../utils/inputSanitizer';

// Updated interfaces to match the new data structure
interface Permission {
  id?: number;
  name: string;
  url: string;
  enabled: boolean;
  isAdminDisabled?: boolean;
}

interface Module {
  id?: number;
  name: string;
  url: string;
  enabled: boolean;
  permissions: Permission[];
  isAdminDisabled?: boolean;
}

interface EditUserProps {
  user: {
    id?: string;
    samId?: string;
    email: string;
    accountType?: string;
    role?: string;
    modules: Module[];
    username?: string;
    designatedOperator?: string;
  } | null;
  onClose: () => void;
  onSave: (updatedUser: {
    id?: string;
    samId?: string;
    email: string;
    accountType: string;
    role: string;
    modules: Module[];
    username?: string;
    designatedOperator: string;
  }) => void;
  isAddMode?: boolean;
  onErrorChange?: (hasErrors: boolean) => void;
}

// Convert to forwardRef to expose handleSave method
const EditUser = forwardRef<{ handleSave: () => void; hasErrors: boolean }, EditUserProps>(
  function EditUserComponent({ user, onSave, isAddMode = false, onErrorChange }, ref) {
    const { t } = useTranslation();
    
    // Get the current logged-in user from IAM
    const currentUserData = useUser();
    
    // Check if current logged-in user has admin privileges (safely)
    const isCurrentUserAdmin = React.useMemo(() => {
      try {
        // Check the user data structure - this might vary based on your IAM implementation
        const userRole = currentUserData?.data?.role;
        
        return userRole === UserRoles.ADMIN;
      } catch (error) {
        console.error('Error determining user admin status:', error);
        return false; // Default to non-admin if there's any error
      }
    }, [currentUserData]);

    const isCurrentUserSuperUser = React.useMemo(() => {
      try {
        // Check the user data structure - this might vary based on your IAM implementation
        const userRole = currentUserData?.data?.role;
        
        return userRole === UserRoles.SUPER_USER;
      } catch (error) {
        console.error('Error determining user admin status:', error);
        return false; // Default to non-admin if there's any error
      }
    }, [currentUserData]);
    
    // Get the current logged-in user's designated operator
    const currentUserDesignatedOperator = React.useMemo(() => {
      try {
        return currentUserData?.data?.designatedOperator || 'ALL';
      } catch (error) {
        console.error('Error determining user designated operator:', error);
        return 'ALL'; // Default to ALL if there's any error
      }
    }, [currentUserData]);
    
    // Fetch designated operators using the hook - only enabled for Admin users
    const { 
      data: designatedOperatorsData, 
      isLoading: isLoadingOperators,
      error: designatedOperatorsError
    } = useDesignatedOperators({}, isCurrentUserAdmin); // Only enable for admin users
    
    // Transform the data to the format expected by the Select component
    const designatedOperatorOptions = React.useMemo(() => {
      // For Super Users, only use their current designated operator
      if (isCurrentUserSuperUser) {
        return [{ id: currentUserDesignatedOperator, label: currentUserDesignatedOperator }];
      }
      
      if (!designatedOperatorsData?.content) {
        return []; // No options available
      }
      
      // Map the API response to the format expected by the Select (excluding 'ALL')
      return designatedOperatorsData.content
        .filter(operator => !operator.disabled) // Filter out disabled operators
        .map(operator => ({
          id: operator.name, // Using name as the ID
          label: operator.name // otherwise use name
        }));
    }, [designatedOperatorsData, isCurrentUserSuperUser, currentUserDesignatedOperator]);
    
    // Track if the user has manually modified permissions (to prevent auto-setting them)
    const hasUserModifiedPermissions = React.useRef<boolean>(false);
    
    // Map account type to UI representation using our constants
    const mapAccountTypeToUI = (accountType?: string): string => {
      if (!accountType) return AccountTypeLabels[AccountTypes.MAILBOX]; // Default to Mailbox
      
      // Account for different case formats and possible inconsistencies
      const normalizedType = accountType.toUpperCase();
      // Check for any variation that includes "IPC" in the account type
      return normalizedType.includes('IPC') 
        ? AccountTypeLabels[AccountTypes.IPC_USER] 
        : AccountTypeLabels[AccountTypes.MAILBOX];
    };
    
    // Map UI representation to account type using our constants
    const mapUIToAccountType = (uiType: string): string => {
      return uiType === AccountTypeLabels[AccountTypes.IPC_USER] 
        ? AccountTypes.IPC_USER 
        : AccountTypes.MAILBOX;
    };
    
    // Map role to UI representation using our constants
    const mapRoleToUI = (role?: string): string => {
      if (!role) return UserRoleLabels[UserRoles.USER];
      
      switch (role) {
        case UserRoles.ADMIN: return UserRoleLabels[UserRoles.ADMIN];
        case UserRoles.SUPER_USER: return UserRoleLabels[UserRoles.SUPER_USER];
        case UserRoles.USER: return UserRoleLabels[UserRoles.USER];
        default: return UserRoleLabels[UserRoles.USER];
      }
    };
    
    // Map UI representation to role using our constants
    const mapUIToRole = (uiRole: string): string => {
      switch (uiRole) {
        case UserRoleLabels[UserRoles.ADMIN]: return UserRoles.ADMIN;
        case UserRoleLabels[UserRoles.SUPER_USER]: return UserRoles.SUPER_USER;
        case UserRoleLabels[UserRoles.USER]: return UserRoles.USER;
        default: return UserRoles.USER;
      }
    };

    const [userType, setUserType] = useState<string>(mapAccountTypeToUI(user?.accountType) || AccountTypeLabels[AccountTypes.MAILBOX]);
    const [designatedOperator, setDesignatedOperator] = useState<string>(
      isCurrentUserSuperUser ? currentUserDesignatedOperator : (user?.designatedOperator || currentUserDesignatedOperator)
    );
    const [email, setEmail] = useState<string>(user?.email || '');
    const [originalEmail, setOriginalEmail] = useState<string>(user?.email || '');
    const [samId, setSamId] = useState<string>(user?.samId || '');
    const [samIdError, setSamIdError] = useState<string | null>(null);
    const [userRole, setUserRole] = useState<string>(mapRoleToUI(user?.role) || UserRoleLabels[UserRoles.USER]);
    const username = user?.samId || '';
    
    // Create a lookup for module toggles based on enabled state
    const initialModuleToggles: Record<string, boolean> = {};
    const [moduleToggles, setModuleToggles] = useState<Record<string, boolean>>({});
    
    // Use our module constants instead of hardcoded strings
    const moduleMap: Record<string, string> = {
      [ModuleNames.ENO1_SAMPLING]: 'ENO1',
      [ModuleNames.E53_SAMPLING]: 'E53',
      [ModuleNames.SAMPLING_PLANNING]: 'SAMPLING',
      [ModuleNames.E55_WEIGHTS]: 'E55',
      [ModuleNames.E09_IBRS]: 'E09',
      [ModuleNames.IPC_TD_ACCOUNTING]: 'IPC_ACC',
      [ModuleNames.TD_ACCOUNTING]: 'TD_ACC'
    };
    
    // Ensure initialModuleToggles has all the necessary keys
    Object.values(moduleMap).forEach(toggleKey => {
      initialModuleToggles[toggleKey] = false;
    });
    
    // Store module and permission IDs to preserve them
    const [moduleIds, setModuleIds] = useState<Record<string, number>>({});
    const [permissionIds, setPermissionIds] = useState<Record<string, Record<string, number>>>({});
    
    // Track which modules and permissions are admin-disabled
    const [adminDisabledModules, setAdminDisabledModules] = useState<Record<string, boolean>>({});
    const [adminDisabledPermissions, setAdminDisabledPermissions] = useState<Record<string, Record<string, boolean>>>({});
    
    // Track current user's accessible permissions for proper default initialization
    const [currentUserAccessiblePermissions, setCurrentUserAccessiblePermissions] = useState<Record<string, string[]>>({});
    const [currentUserEnabledModules, setCurrentUserEnabledModules] = useState<Record<string, boolean>>({});
    const [currentUserAdminDisabledPermissions, setCurrentUserAdminDisabledPermissions] = useState<Record<string, Record<string, boolean>>>({});
    const [currentUserAdminDisabledModules, setCurrentUserAdminDisabledModules] = useState<Record<string, boolean>>({});
    
    // Initialize current user's accessible permissions and modules (for add mode default initialization)
    useEffect(() => {
      // Only load current user's permissions in add mode
      if (isAddMode && currentUserData?.data) {
        const accessiblePerms: Record<string, string[]> = {};
        const enabledModules: Record<string, boolean> = {};
        const adminDisabledPerms: Record<string, Record<string, boolean>> = {};
        const adminDisabledMods: Record<string, boolean> = {};
        
        // Process current user's modules
        currentUserData.data.modules?.forEach(module => {
          const moduleName = module.name;
          const toggleKey = moduleMap[moduleName];
          
          // Track enabled modules
          if (toggleKey) {
            enabledModules[toggleKey] = module.enabled;
            
            // Track admin-disabled modules
            if (module.isAdminDisabled) {
              adminDisabledMods[toggleKey] = true;
            }
          }
          
          // Process permissions
          if (module.permissions) {
            const enabledPerms: string[] = [];
            
            module.permissions.forEach(permission => {
              if (permission.enabled) {
                enabledPerms.push(permission.name);
              }
              
              // Track admin-disabled permissions
              if (permission.isAdminDisabled) {
                if (!adminDisabledPerms[moduleName]) {
                  adminDisabledPerms[moduleName] = {};
                }
                adminDisabledPerms[moduleName][permission.name] = true;
              }
            });
            
            accessiblePerms[moduleName] = enabledPerms;
          }
        });
        
        setCurrentUserAccessiblePermissions(accessiblePerms);
        setCurrentUserEnabledModules(enabledModules);
        setCurrentUserAdminDisabledPermissions(adminDisabledPerms);
        setCurrentUserAdminDisabledModules(adminDisabledMods);
      }
    }, [currentUserData, isAddMode]);
    
    // Initialize defaults for new users based on current logged-in user's permissions
    useEffect(() => {
      // Only in add mode and when we have current user permissions loaded
      if (isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin && 
          Object.keys(currentUserAccessiblePermissions).length > 0 &&
          !hasUserModifiedPermissions.current) {
        
        // Initialize module toggles based on current user's enabled modules
        const newModuleToggles = { ...initialModuleToggles };
        Object.entries(currentUserEnabledModules).forEach(([moduleKey, isEnabled]) => {
          newModuleToggles[moduleKey] = isEnabled;
        });
        setModuleToggles(newModuleToggles);
        
        // Initialize permissions based on current user's accessible permissions
        const newSelectedPermissions: Record<string, string[]> = {};
        Object.keys(modulePermissions).forEach(moduleName => {
          newSelectedPermissions[moduleName] = [];
        });
        Object.entries(currentUserAccessiblePermissions).forEach(([moduleName, accessiblePerms]) => {
          // Filter out admin-disabled permissions from the accessible permissions
          const filteredPerms = accessiblePerms.filter(permission => 
            !currentUserAdminDisabledPermissions[moduleName]?.[permission]
          );
          newSelectedPermissions[moduleName] = filteredPerms;
        });
        setSelectedPermissions(newSelectedPermissions);
        
        // CRITICAL: Inherit admin-disabled state from current user
        // This ensures that when Super User creates a new user, they cannot grant
        // permissions/modules that are admin-disabled for them
        setAdminDisabledModules(currentUserAdminDisabledModules);
        setAdminDisabledPermissions(currentUserAdminDisabledPermissions);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserAccessiblePermissions, currentUserEnabledModules, currentUserAdminDisabledModules, currentUserAdminDisabledPermissions, isAddMode]);
    
    // Initialize module toggles and store IDs from user data
    useEffect(() => {
      // Start with default module and permission IDs
      let newModuleIds = { ...ModuleIds } as Record<string, number>;
      let newPermissionIds = { ...PermissionIds } as Record<string, Record<string, number>>;
      const newModuleToggles = { ...initialModuleToggles };
      const newAdminDisabledModules: Record<string, boolean> = {};
      const newAdminDisabledPermissions: Record<string, Record<string, boolean>> = {};
      
      if (user?.modules) {
        // If we have user modules, merge the IDs from user data with defaults
        user.modules.forEach(module => {
          // Store module ID (overriding the default if available)
          if (module.id) {
            newModuleIds[module.name] = module.id;
          }
          
          // Store permission IDs (overriding defaults if available)
          module.permissions.forEach(permission => {
            if (!newPermissionIds[module.name]) {
              newPermissionIds[module.name] = {};
            }
            
            if (permission.id) {
              newPermissionIds[module.name][permission.name] = permission.id;
            }
            
            // Track admin-disabled permissions
            if (permission.isAdminDisabled) {
              if (!newAdminDisabledPermissions[module.name]) {
                newAdminDisabledPermissions[module.name] = {};
              }
              newAdminDisabledPermissions[module.name][permission.name] = true;
            }
          });
          
          // Set module toggle state based on module.enabled property
          const moduleKey = moduleMap[module.name];
          if (moduleKey) {
            newModuleToggles[moduleKey] = module.enabled;
            
            // Track admin-disabled modules
            if (module.isAdminDisabled) {
              newAdminDisabledModules[moduleKey] = true;
            }
          }
        });
      }
      
      setModuleIds(newModuleIds);
      setPermissionIds(newPermissionIds);
      setModuleToggles(newModuleToggles);
      setAdminDisabledModules(newAdminDisabledModules);
      setAdminDisabledPermissions(newAdminDisabledPermissions);
    }, [user]);
  
    
    // Module permissions for each section
    const modulePermissions: Record<string, string[]> = {
      'eNO1 Sampling': [
        'Access to the eNO1 module',
        'eNO1 View Samples',
        'eNO1 Edit Samples',
        'Delete eNO1 Files',
        'eNO1 Upload',
        'eNO1 Status',
        'NO2 Report',
        'NO3 Report',
        'NO3 Signed Forms',
        'Accuracy Report',
        'e55 Comparison',
        'eNO1 Playground'
      ],
      'e53 Sampling': [
        'Access to the e53 sampling module',
        'e53 View Samples',
        'e53 Edit Samples',
        'Delete e53 files',
        'e53 Upload',
        'View e53 status',
        'CN54 Report'
      ],
      'Sampling Planning': [
        'Access to the Sampling Planning module'
      ],
      'e55 Weights': [
        'View Despatches',
        'Edit Despatches',
        'Delete e55 Files',
        'e55 Upload',
        'e55 Status',
        'CN56 Report',
        'e55 items-kg report'
      ],
      'e09 IBRS': [
        'View Despatches',
        'Edit Despatches',
        'Delete e09 Files',
        'e09 Upload',
        'e09 Status',
        'CN10 Report',
        'CN19 Report'
      ],
      'IPC TD Accounting': [
        'Access to IPC Accounting module'
      ],
      'UPU TD Accounting': [
        'Access to the UPU TD Accounting module'
      ]
    };

    // Map module toggles to permission sections
    const moduleToPermissionMap = {
      'ENO1': 'eNO1 Sampling',
      'E53': 'e53 Sampling',
      'SAMPLING': 'Sampling Planning',
      'E55': 'e55 Weights',
      'E09': 'e09 IBRS',
      'IPC_ACC': 'IPC TD Accounting',
      'TD_ACC': 'UPU TD Accounting'
    };

    // Effect to update permissions when module toggles change or role changes to Super User
    useEffect(() => {
      // Only auto-enable all modules when role first changes to Admin or Super User
      // AND we're adding a new user (not editing an existing one)
      if ((userRole === UserRoleLabels[UserRoles.ADMIN] || userRole === UserRoleLabels[UserRoles.SUPER_USER]) && 
          !hasUserModifiedPermissions.current && isAddMode) {
        
        // For Super Users: only enable modules and permissions that they have access to
        // For Admins: enable all modules and permissions (they have full access)
        const newModuleToggles = { ...moduleToggles };
        const newSelectedPermissions = { ...selectedPermissions };
        
        if (isCurrentUserSuperUser && !isCurrentUserAdmin) {
          // Super User: only enable what they have access to
          Object.entries(currentUserEnabledModules).forEach(([moduleKey, isEnabled]) => {
            newModuleToggles[moduleKey] = isEnabled;
          });
          
          // Enable only permissions that the current Super User has (excluding admin-disabled ones)
          Object.entries(currentUserAccessiblePermissions).forEach(([moduleName, accessiblePerms]) => {
            const filteredPerms = accessiblePerms.filter(permission => 
              !currentUserAdminDisabledPermissions[moduleName]?.[permission]
            );
            newSelectedPermissions[moduleName] = filteredPerms;
          });
          
          // CRITICAL: Also inherit admin-disabled state from current user
          // This ensures that Super Users cannot grant admin-disabled permissions to others
          setAdminDisabledModules(currentUserAdminDisabledModules);
          setAdminDisabledPermissions(currentUserAdminDisabledPermissions);
        } else if (isCurrentUserAdmin) {
          // Admin: enable all modules by default
          Object.keys(newModuleToggles).forEach(moduleKey => {
            newModuleToggles[moduleKey] = true;
          });
          
          // Enable all permissions by default
          Object.entries(modulePermissions).forEach(([moduleName, permissions]) => {
            newSelectedPermissions[moduleName] = permissions;
          });
        }
        
        setModuleToggles(newModuleToggles);
        setSelectedPermissions(newSelectedPermissions);
        return;
      }
      
      // Only update permissions that haven't been manually modified
      // Skip this logic for Super Users in add mode to prevent loops
      if (!hasUserModifiedPermissions.current && !(isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin)) {
        // For all roles, ensure permissions match module toggle state
        Object.entries(moduleToggles).forEach(([moduleKey, isEnabled]) => {
          if (isEnabled) {
            const permissionSection = moduleToPermissionMap[moduleKey as keyof typeof moduleToPermissionMap];
            if (permissionSection && selectedPermissions[permissionSection]?.length === 0) {
              // If module is enabled but has no permissions, enable all permissions
              // except those that are admin-disabled for non-admin users
              setSelectedPermissions(prev => {
                let allowedPermissions: string[];
                
                if (isCurrentUserAdmin) {
                  // Admin: enable all permissions
                  allowedPermissions = [...modulePermissions[permissionSection]];
                } else {
                  // Other users: enable permissions except admin-disabled ones
                  allowedPermissions = modulePermissions[permissionSection].filter(permission => 
                    !adminDisabledPermissions[permissionSection]?.[permission]
                  );
                }
                
                return {
                  ...prev,
                  [permissionSection]: allowedPermissions
                };
              });
            }
          } else {
            // If module is disabled, clear all permissions
            const permissionSection = moduleToPermissionMap[moduleKey as keyof typeof moduleToPermissionMap];
            if (permissionSection) {
              setSelectedPermissions(prev => ({
                ...prev,
                [permissionSection]: []
              }));
            }
          }
        });
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole, moduleToggles, isAddMode, currentUserAccessiblePermissions, currentUserEnabledModules, currentUserAdminDisabledModules, currentUserAdminDisabledPermissions]);
    
    // Effect to validate email when userType changes to Mailbox
    useEffect(() => {
      // Check if we need to validate the email field
      if (userType === AccountTypeLabels[AccountTypes.MAILBOX] && email.trim() === '' && isAddMode) {
        // Set focus on the email input if it's empty and required
        const emailInput = document.querySelector('[data-testid="email-input"] input');
        if (emailInput) {
          (emailInput as HTMLInputElement).focus();
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType]);
    
    // Effect to update username from email for Mailbox users
    useEffect(() => {
      // For Mailbox users in add mode, if they don't have a SAM ID, derive username from email
      if (isAddMode && userType === AccountTypeLabels[AccountTypes.MAILBOX] && !samId.trim() && email.includes('@')) {
        // Extract username part from email (before the @ symbol)
        const usernameFromEmail = email.split('@')[0];
        // Don't set SAM ID for Mailbox users since the field is disabled
        // We'll handle this in the backend or during submission
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, userType, isAddMode]);
    
    // Effect to sanitize initial samId if it contains dangerous content
    useEffect(() => {
      if (user?.samId) {
        const initialSamId = user.samId;
        if (hasDangerousInput(initialSamId)) {
          const sanitized = sanitizeTextInput(initialSamId);
          setSamId(sanitized);
          setSamIdError('SAM ID contained invalid characters and has been sanitized');
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.samId]);

    // Effect to focus on SAM ID field in add mode on initial render
    useEffect(() => {
      if (isAddMode) {
        // Set focus on the SAM ID input when adding a new user
        const samIdInput = document.querySelector('[data-testid="username-input"] input');
        if (samIdInput) {
          (samIdInput as HTMLInputElement).focus();
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    // Initialize selected permissions
    const initialSelectedPermissions: Record<string, string[]> = {};
    
    // Populate from user.modules if available
    if (user?.modules) {
      user.modules.forEach(module => {
        // Get the enabled permissions based on the data
        const enabledPermissions = module.permissions
          .filter(permission => permission.enabled)
          .map(permission => permission.name);
        
        initialSelectedPermissions[module.name] = enabledPermissions;
      });
    }
    
    // If no permissions found for a module, initialize with empty array
    Object.keys(modulePermissions).forEach(moduleName => {
      if (!initialSelectedPermissions[moduleName]) {
        initialSelectedPermissions[moduleName] = [];
      }
    });
    
    const [selectedPermissions, setSelectedPermissions] = useState<Record<string, string[]>>(initialSelectedPermissions);

    // Add validation errors state
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    // Add a flag to track if component is initialized
    const [isInitialized, setIsInitialized] = useState(false);

    // Validate email format
    const isValidEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    const handleUserTypeChange = (type: string) => {
      // In edit mode, don't allow changing account type
      if (!isAddMode) {
        return;
      }
      
      setUserType(type);
      
      // If switching from Mailbox to IPC User, reset email to original
      if (type === AccountTypeLabels[AccountTypes.IPC_USER]) {
        setEmail(originalEmail);
      } else if (type === AccountTypeLabels[AccountTypes.MAILBOX]) {
        // Reset SAM ID when switching to Mailbox type
        setSamId('');
        setSamIdError(null);
      } else {
        // Reset error when switching to IPC User type
        setSamIdError(null);
      }
      
      // If changing to Mailbox type, user cannot be IPC Admin or Super User
      if (type === AccountTypeLabels[AccountTypes.MAILBOX] && 
         (userRole === UserRoleLabels[UserRoles.ADMIN] || userRole === UserRoleLabels[UserRoles.SUPER_USER])) {
        setUserRole(UserRoleLabels[UserRoles.USER]);
      }
      
      // Set focus to appropriate field based on user type
      if (isAddMode) {
        setTimeout(() => {
          if (type === AccountTypeLabels[AccountTypes.IPC_USER]) {
            // For IPC Users, focus on the SAM ID field
            const samIdInput = document.querySelector('[data-testid="username-input"] input');
            if (samIdInput) {
              (samIdInput as HTMLInputElement).focus();
            }
          } else if (type === AccountTypeLabels[AccountTypes.MAILBOX] && email.trim() === '') {
            // For Mailbox users, focus on the email field if it's empty
            const emailInput = document.querySelector('[data-testid="email-input"] input');
            if (emailInput) {
              (emailInput as HTMLInputElement).focus();
            }
          }
        }, 0);
      }
      
      // Only update designatedOperator automatically if the current user is not an admin
      if (!isCurrentUserAdmin && !isCurrentUserSuperUser) {
        if (type === AccountTypeLabels[AccountTypes.IPC_USER]) {
          setDesignatedOperator(currentUserDesignatedOperator);
        } else {
          setDesignatedOperator(currentUserDesignatedOperator);
        }
      }
    };

    const handleUserRoleChange = (role: string) => {
      // Only allow changing roles if the current user has admin privileges
      if (!isCurrentUserAdmin && !isCurrentUserSuperUser) {
        return;
      }
      
      // If changing to IPC Admin or Super User, user must be IPC User type
      if ((role === UserRoleLabels[UserRoles.ADMIN] || role === UserRoleLabels[UserRoles.SUPER_USER]) && 
          userType === AccountTypeLabels[AccountTypes.MAILBOX]) {
        setUserType(AccountTypeLabels[AccountTypes.IPC_USER]);
      }
      
      const oldRole = userRole;
      setUserRole(role);
      
      // If changing to Super User, set the designated operator to the current user's value
      // BUT only if the current user is NOT an admin (admins should keep their manually selected value)
      if (role === UserRoleLabels[UserRoles.SUPER_USER] && oldRole !== UserRoleLabels[UserRoles.SUPER_USER] && !isCurrentUserAdmin) {
        setDesignatedOperator(currentUserDesignatedOperator);
      }
      
      // If changing to Admin or Super User, initialize all modules and permissions
      // but allow them to be customized afterward
      if (role === UserRoleLabels[UserRoles.ADMIN] || role === UserRoleLabels[UserRoles.SUPER_USER]) {
        
        // For Super Users creating users: only enable what they have access to
        // For Admins creating users: enable all modules and permissions
        const allModulesEnabled = { ...moduleToggles };
        const allEnabledPermissions = { ...selectedPermissions };
        
        if (isCurrentUserSuperUser && !isCurrentUserAdmin && isAddMode) {
          // Super User: only enable what they have access to
          Object.entries(currentUserEnabledModules).forEach(([moduleKey, isEnabled]) => {
            allModulesEnabled[moduleKey] = isEnabled;
          });
          
          // Enable only permissions that the current Super User has (excluding admin-disabled ones)
          Object.entries(currentUserAccessiblePermissions).forEach(([moduleName, accessiblePerms]) => {
            const filteredPerms = accessiblePerms.filter(permission => 
              !currentUserAdminDisabledPermissions[moduleName]?.[permission]
            );
            allEnabledPermissions[moduleName] = filteredPerms;
          });
          
          // CRITICAL: Also inherit admin-disabled state from current user
          // This ensures that Super Users cannot grant admin-disabled permissions to others
          setAdminDisabledModules(currentUserAdminDisabledModules);
          setAdminDisabledPermissions(currentUserAdminDisabledPermissions);
        } else if (isCurrentUserAdmin) {
          // Admin: enable all modules
          Object.keys(allModulesEnabled).forEach(moduleKey => {
            allModulesEnabled[moduleKey] = true;
          });
          
          // Admin: enable all permissions
          Object.entries(modulePermissions).forEach(([moduleName, permissions]) => {
            allEnabledPermissions[moduleName] = permissions;
          });
        } else {
          // For other cases (non-add mode or different scenarios), keep existing logic
          Object.keys(allModulesEnabled).forEach(moduleKey => {
            // Only enable if not admin-disabled OR if current user is an admin
            if (!adminDisabledModules[moduleKey] || isCurrentUserAdmin) {
              allModulesEnabled[moduleKey] = true;
            }
          });
          
          Object.entries(modulePermissions).forEach(([moduleName, permissions]) => {
            const toggleKey = Object.entries(moduleToPermissionMap).find(
              ([_, value]) => value === moduleName
            )?.[0];
            
            if (toggleKey && adminDisabledModules[toggleKey] && !isCurrentUserAdmin) {
              return;
            }
            
            const enabledPermissions = [...permissions].filter(permission => 
              isCurrentUserAdmin || !adminDisabledPermissions[moduleName]?.[permission]
            );
            
            allEnabledPermissions[moduleName] = enabledPermissions;
          });
        }
        
        setModuleToggles(allModulesEnabled);
        setSelectedPermissions(allEnabledPermissions);
        
        // Reset the flag to false - permissions were automatically set but can be modified
        hasUserModifiedPermissions.current = false;
      }
      
      // Only non-admin users should have designatedOperator auto-updated
      if (!isCurrentUserAdmin && !isCurrentUserSuperUser) {
        if (role !== UserRoleLabels[UserRoles.ADMIN]) {
          if (userType === AccountTypeLabels[AccountTypes.IPC_USER]) {
            setDesignatedOperator(currentUserDesignatedOperator);
          } else {
            setDesignatedOperator(currentUserDesignatedOperator);
          }
        }
      }
    };

    // Mark component as initialized after initial load
    useEffect(() => {
      // Use a short delay to ensure all data is loaded
      const timer = setTimeout(() => {
        setIsInitialized(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }, []);

    // Validate module permissions
    const validateModules = () => {
      // Skip validation until component is fully initialized
      if (!isInitialized) {
        return true;
      }

      const newErrors: Record<string, string> = {};
      
      // Check each enabled module to ensure it has at least one permission enabled
      Object.entries(moduleToggles).forEach(([moduleKey, isEnabled]) => {
        if (isEnabled) {
          const permissionSection = moduleToPermissionMap[moduleKey as keyof typeof moduleToPermissionMap];
          
          if (permissionSection) {
            // Check if the module has any permissions enabled
            const hasEnabledPermissions = selectedPermissions[permissionSection]?.length > 0;
            
            if (!hasEnabledPermissions) {
              newErrors[moduleKey] = `${moduleKey.replace('_', ' ')} module is enabled but has no permissions selected.`;
            }
          }
        }
      });
      
      setValidationErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    // Validate on module toggles or permission changes
    useEffect(() => {
      // Only run validation if component is initialized
      if (isInitialized) {
        validateModules();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleToggles, selectedPermissions, isInitialized]);

    const handleModuleToggle = (module: string) => () => {
      // Check if the module is disabled by an admin and current user is not an admin
      if (adminDisabledModules[module] && !isCurrentUserAdmin) {
        // Non-admin users cannot modify admin-disabled modules
        return;
      }
      
      // For Super Users (not admins): check if they have access to this module
      if (isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin) {
        // Check if current user has this module admin-disabled
        const currentUserHasAdminDisabled = currentUserAdminDisabledModules[module] === true;
        // Check if current user has this module enabled
        const currentUserHasAccess = currentUserEnabledModules[module] === true;
        // If trying to enable a module that the current Super User doesn't have access to or is admin-disabled
        if (!moduleToggles[module] && (currentUserHasAdminDisabled || !currentUserHasAccess)) {
          return; // Don't allow enabling modules they don't have or are admin-disabled
        }
      }
      
      // Flag that user has manually modified permissions
      hasUserModifiedPermissions.current = true;
      
      const newToggles = {
        ...moduleToggles,
        [module]: !moduleToggles[module]
      };
      
      setModuleToggles(newToggles);
      
      // Get the corresponding permission section for this module
      const permissionSection = moduleToPermissionMap[module as keyof typeof moduleToPermissionMap];
      
      if (permissionSection) {
        if (newToggles[module]) {
          // If enabling the module, enable permissions
          setSelectedPermissions(prev => {
            let enabledPermissions: string[];
            
            if (isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin) {
              // Super User in add mode: only enable permissions they have access to
              // AND that are not admin-disabled for them
              enabledPermissions = (currentUserAccessiblePermissions[permissionSection] || []).filter(permission => 
                !currentUserAdminDisabledPermissions[permissionSection]?.[permission]
              );
            } else if (isCurrentUserAdmin) {
              // Admin: enable all permissions
              enabledPermissions = [...modulePermissions[permissionSection]];
            } else {
              // Other users: enable permissions except admin-disabled ones
              enabledPermissions = modulePermissions[permissionSection].filter(permission => 
                !adminDisabledPermissions[permissionSection]?.[permission]
              );
            }
            
            return {
              ...prev,
              [permissionSection]: enabledPermissions
            };
          });
        } else {
          // If disabling the module, clear all permissions
          setSelectedPermissions(prev => ({
            ...prev,
            [permissionSection]: []
          }));
        }
      }
    };

    const handlePermissionChange = (module: string, permission: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      // Check if this permission is admin-disabled and the current user is not an admin
      if (adminDisabledPermissions[module]?.[permission] && !isCurrentUserAdmin) {
        // Non-admin users (including Super Users) cannot modify admin-disabled permissions
        return;
      }
      
      // For Super Users (not admins) in add mode: check if they have access to this permission
      if (isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin && event.target.checked) {
        // Check if current user has this permission admin-disabled
        const currentUserHasAdminDisabled = currentUserAdminDisabledPermissions[module]?.[permission] === true;
        // Check if current user has access to this permission
        const currentUserHasPermission = currentUserAccessiblePermissions[module]?.includes(permission);
        // If trying to enable a permission that is admin-disabled or the current Super User doesn't have access to
        if (currentUserHasAdminDisabled || !currentUserHasPermission) {
          return; // Don't allow granting permissions they don't have or are admin-disabled
        }
      }
      
      // Flag that user has manually modified permissions
      hasUserModifiedPermissions.current = true;
      
      if (!selectedPermissions[module]) {
        setSelectedPermissions({
          ...selectedPermissions,
          [module]: [permission]
        });
        return;
      }

      if (event.target.checked) {
        setSelectedPermissions({
          ...selectedPermissions,
          [module]: [...selectedPermissions[module], permission]
        });
      } else {
        setSelectedPermissions({
          ...selectedPermissions,
          [module]: selectedPermissions[module].filter(p => p !== permission)
        });
      }
    };

    const handleSelectAll = (module: string) => () => {
      // Determine available permissions based on user type
      let availablePermissions: string[];
      
      if (isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin) {
        // Super User in add mode: only available permissions are what they have access to
        // AND that are not admin-disabled for them
        availablePermissions = (currentUserAccessiblePermissions[module] || []).filter(permission => 
          !currentUserAdminDisabledPermissions[module]?.[permission]
        );
      } else if (isCurrentUserAdmin) {
        // Admin: all permissions are available
        availablePermissions = modulePermissions[module];
      } else {
        // Other users: permissions except admin-disabled ones
        availablePermissions = modulePermissions[module].filter(permission => 
          !adminDisabledPermissions[module]?.[permission]
        );
      }
      
      if (selectedPermissions[module].length === availablePermissions.length) {
        // Deselect all permissions
        if (!isCurrentUserAdmin) {
          // Keep admin-disabled permissions selected
          setSelectedPermissions({
            ...selectedPermissions,
            [module]: modulePermissions[module].filter(permission => 
              adminDisabledPermissions[module]?.[permission] && selectedPermissions[module].includes(permission)
            )
          });
        } else {
          // Admin users can deselect all
          setSelectedPermissions({
            ...selectedPermissions,
            [module]: []
          });
        }
      } else {
        // Select all available permissions
        setSelectedPermissions({
          ...selectedPermissions,
          [module]: availablePermissions
        });
      }
    };

    // Permission URL maps
    const permissionUrlMap: Record<string, Record<string, string>> = {
      'eNO1 Sampling': {
        'Access to the eNO1 module': '/eno1',
        'eNO1 View Samples': '/eno1/samples',
        'eNO1 Edit Samples': '/eno1/samples/edit',
        'Delete eNO1 Files': '/eno1/files/delete',
        'eNO1 Upload': '/eno1/upload',
        'eNO1 Status': '/eno1/status',
        'NO2 Report': '/eno1/reports/no2',
        'NO3 Report': '/eno1/reports/no3',
        'NO3 Signed Forms': '/eno1/reports/no3/signed',
        'Accuracy Report': '/eno1/reports/accuracy',
        'e55 Comparison': '/eno1/reports/e55-comparison',
        'eNO1 Playground': '/eno1/playground'
      },
      'e53 Sampling': {
        'Access to the e53 sampling module': '/e53',
        'e53 View Samples': '/e53/samples',
        'e53 Edit Samples': '/e53/samples/edit',
        'Delete e53 files': '/e53/files/delete',
        'e53 Upload': '/e53/upload',
        'View e53 status': '/e53/status',
        'CN54 Report': '/e53/reports/cn54'
      },
      'Sampling Planning': {
        'Access to the Sampling Planning module': '/sampling-planning'
      },
      'e55 Weights': {
        'View Despatches': '/e55/despatches',
        'Edit Despatches': '/e55/despatches/edit',
        'Delete e55 Files': '/e55/files/delete',
        'e55 Upload': '/e55/upload',
        'e55 Status': '/e55/status',
        'CN56 Report': '/e55/reports/cn56',
        'e55 items-kg report': '/e55/reports/items-kg'
      },
      'e09 IBRS': {
        'View Despatches': '/e09/despatches',
        'Edit Despatches': '/e09/despatches/edit',
        'Delete e09 Files': '/e09/files/delete',
        'e09 Upload': '/e09/upload',
        'e09 Status': '/e09/status',
        'CN10 Report': '/e09/reports/cn10',
        'CN19 Report': '/e09/reports/cn19'
      },
      'IPC TD Accounting': {
        'Access to IPC Accounting module': '/ipc-td-accounting'
      },
      'UPU TD Accounting': {
        'Access to the UPU TD Accounting module': '/upu-td-accounting'
      }
    };

    const handleSave = () => {
      // Validate that email is filled and properly formatted for Mailbox type users
      if (userType === AccountTypeLabels[AccountTypes.MAILBOX]) {
        if (!email.trim()) {
          alert(t('profile.userManagement.emailRequired', 'Email is required for Mailbox users'));
          return;
        }
        
        if (!isValidEmail(email)) {
          alert(t('profile.userManagement.invalidEmail', 'Please enter a valid email address'));
          return;
        }
      }
      
      // Validate that SAM ID is filled for new IPC users
      if (isAddMode && userType === AccountTypeLabels[AccountTypes.IPC_USER] && !samId.trim()) {
        alert(t('profile.userManagement.samIdRequired', 'SAM ID is required for IPC User type'));
        return;
      }
      
      // Validate SAM ID doesn't contain dangerous content
      // Error is already shown in the field via samIdError state, just prevent saving
      if (isAddMode && userType === AccountTypeLabels[AccountTypes.IPC_USER] && samId) {
        if (hasDangerousInput(samId) || !isValidSamId(samId)) {
          return;
        }
      }
      
      // Validate modules have permissions if enabled
      if (!validateModules()) {
        return;
      }
      
      // For IPC users, always use the original email
      const emailToSave = userType === AccountTypeLabels[AccountTypes.IPC_USER] ? originalEmail : email;
      
      // Base user data - CRITICAL: Ensure designatedOperator is included
      // Sanitize samId before saving to prevent XSS
      const sanitizedSamId = samId ? sanitizeTextInput(samId) : samId;
      const userData = {
        samId: userType === AccountTypeLabels[AccountTypes.MAILBOX] ? undefined : sanitizedSamId,
        email: emailToSave,
        accountType: mapUIToAccountType(userType),
        role: mapUIToRole(userRole),
        designatedOperator: designatedOperator, // Make sure designatedOperator is included here
        username: username,
      };
      
      // For add mode, create user without any id fields
      if (isAddMode) {
        const addUserData = {
          ...userData,
          designatedOperator: designatedOperator, // Explicitly include designatedOperator again for clarity
          modules: Object.keys(ModuleNames).map(moduleKey => {
            const moduleName = ModuleNames[moduleKey as keyof typeof ModuleNames];
            const moduleUrl = ModuleUrls[moduleName as keyof typeof ModuleUrls];
            
            // Find the module toggle key for this module
            const toggleKey = Object.keys(ModuleToggleKeys).find(
              key => ModuleToggleKeys[key as keyof typeof ModuleToggleKeys] === moduleName
            ) || '';
            
            // Determine if the module is enabled based on current toggle state
            const isEnabled = moduleToggles[toggleKey] || false;
            
            // Get permissions for this module from our modulePermissions object
            const permissionsForModule = selectedPermissions[moduleName] || [];
            
            // Only include permissions that are defined in modulePermissions (not from DefaultModules)
            const modulePermissionsList = modulePermissions[moduleName] || [];
            
            // Create a module object WITHOUT any id field
            const moduleObj: any = {
              name: moduleName,
              url: moduleUrl,
              enabled: isEnabled,
              permissions: modulePermissionsList.map(permission => {
                const permissionEnabled = permissionsForModule.includes(permission);
                
                // IMPORTANT: Preserve the original adminDisabled state for Super Users
                // For non-admin users, we keep the original adminDisabled flag from the stored state
                // For admin users, we can set/unset the flag based on their actions
                let isAdminDisabled = false;

                if (isCurrentUserSuperUser) {
                  // For Super Users, preserve the original admin-disabled state
                  // Super Users cannot modify the admin-disabled status
                  isAdminDisabled = adminDisabledPermissions[moduleName]?.[permission] || false;
                } else if (isCurrentUserAdmin) {
                  // For Admins: When they disable a permission, it becomes admin-disabled
                  // This means that Super Users and regular users cannot enable this permission
                  isAdminDisabled = !permissionEnabled;
                } else {
                  // For regular users, keep the original admin-disabled state
                  // Regular users cannot modify admin-disabled permissions
                  isAdminDisabled = adminDisabledPermissions[moduleName]?.[permission] || false;
                }
                
                // Create permission object WITHOUT any id field
                return {
                  name: permission,
                  url: permissionUrlMap[moduleName]?.[permission] || '',
                  enabled: permissionEnabled,
                  isAdminDisabled
                };
              })
            };
            
            // Determine the admin-disabled state for the module
            let moduleIsAdminDisabled = false;

            if (isCurrentUserAdmin) {
              // For Admins, if this module was previously admin-disabled, preserve that state
              // unless the admin has now enabled it
              const wasAdminDisabled = adminDisabledModules[toggleKey] || false;
              // If it was admin-disabled and is now enabled, un-admin-disable it
              // If it was not admin-disabled and is now disabled, admin-disable it
              moduleIsAdminDisabled = !isEnabled;
            } else {
              // For all other users, preserve the original admin-disabled state
              moduleIsAdminDisabled = adminDisabledModules[toggleKey] || false;
            }
            
            moduleObj.isAdminDisabled = moduleIsAdminDisabled;
            
            return moduleObj;
          })
        };
        
        // Pass the add user data to the parent component
        onSave(addUserData);
      } else {
        // For edit mode, include the existing IDs
        const editUserData = {
          id: user?.id || '',
          ...userData,
          designatedOperator: designatedOperator, // Explicitly include designatedOperator again for clarity
          modules: Object.keys(ModuleNames).map(moduleKey => {
            const moduleName = ModuleNames[moduleKey as keyof typeof ModuleNames];
            const moduleUrl = ModuleUrls[moduleName as keyof typeof ModuleUrls];
            
            // Find the module toggle key for this module
            const toggleKey = Object.keys(ModuleToggleKeys).find(
              key => ModuleToggleKeys[key as keyof typeof ModuleToggleKeys] === moduleName
            ) || '';
            
            // Get the module ID if we have it stored
            const moduleId = moduleIds[moduleName] || 0;
            
            // Determine if the module is enabled from current toggle state
            const isEnabled = moduleToggles[toggleKey] || false;
            
            // Get permissions for this module
            const permissionSection = moduleToPermissionMap[toggleKey as keyof typeof moduleToPermissionMap];
            const permissionsForModule = permissionSection ? selectedPermissions[permissionSection] || [] : [];
            
            // Get all possible permissions for this module from our constants
            const allModulePermissions = modulePermissions[moduleName] || [];
            
            // Map permissions with their IDs if available
            const permissions = allModulePermissions.map(permission => {
              const permissionEnabled = permissionsForModule.includes(permission);
              
              // If current user is admin and permission is disabled, mark as admin-disabled
              // For other users, preserve any existing admin-disabled flags
              const isAdminDisabled = isCurrentUserAdmin 
                ? !permissionEnabled  // Admin sets admin-disabled when disabling a permission
                : adminDisabledPermissions[moduleName]?.[permission] || false; // Others preserve existing flags
              
              return {
                id: (permissionIds[moduleName] && permissionIds[moduleName][permission]) || 0,
                name: permission,
                url: permissionUrlMap[moduleName]?.[permission] || '',
                enabled: permissionEnabled,
                isAdminDisabled
              };
            });
            
            // Determine the admin-disabled state for the module
            // Preserve the original adminDisabled flag for modules too
            // For non-admin users, we keep the original adminDisabled flag from the stored state
            // For admin users, we can set/unset the flag based on their actions
            let moduleIsAdminDisabled = false;

            if (isCurrentUserSuperUser) {
              // For Super Users, preserve the original admin-disabled state
              // Super Users cannot modify admin-disabled modules
              moduleIsAdminDisabled = adminDisabledModules[toggleKey] || false;
            } else if (isCurrentUserAdmin) {
              // For Admins: When they disable a module, it becomes admin-disabled
              // This prevents Super Users and regular users from enabling this module
              moduleIsAdminDisabled = !isEnabled;
            } else {
              // For regular users, keep the original admin-disabled state
              // Regular users cannot modify admin-disabled modules
              moduleIsAdminDisabled = adminDisabledModules[toggleKey] || false;
            }
            
            return {
              id: moduleId,
              name: moduleName,
              url: moduleUrl,
              enabled: isEnabled,
              permissions: permissions,
              isAdminDisabled: moduleIsAdminDisabled
            };
          })
        };
        
        // Pass the edit user data to the parent component
        onSave(editUserData);
      }
    };

    // Expose handleSave method and hasErrors via ref
    useImperativeHandle(ref, () => ({
      handleSave,
      hasErrors: Object.keys(validationErrors).length > 0
    }));

    // Notify parent of validation errors
    useEffect(() => {
      if (isInitialized && onErrorChange) {
        onErrorChange(Object.keys(validationErrors).length > 0);
      }
    }, [validationErrors, isInitialized, onErrorChange]);

    // Check if user can edit designatedOperator (only admins can edit and Super Users cannot)
    const canEditDesignatedOperator = isCurrentUserAdmin && !isCurrentUserSuperUser;

    // Effect to update designatedOperator if current value is not in the list after data is loaded
    useEffect(() => {
      // Skip for Super Users - they already have their operator fixed
      if (isCurrentUserSuperUser) {
        return;
      }
      
      if (designatedOperatorsData?.content && designatedOperator) {
        // Check if the current value exists in the options
        const isValidOperator = designatedOperatorOptions.some(option => option.id === designatedOperator);
        
        // If not valid (including if it's 'ALL') and we have options, set to first available operator
        if (!isValidOperator && designatedOperatorOptions.length > 0) {
          setDesignatedOperator(designatedOperatorOptions[0].id);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [designatedOperatorsData, designatedOperatorOptions, isCurrentUserSuperUser]);
    
    // Effect to prevent "ALL" from being selected
    useEffect(() => {
      // Skip for Super Users - they already have their operator fixed
      if (isCurrentUserSuperUser) {
        return;
      }
      
      // If current value is "ALL" and we have other options, change it to the first available operator
      if (designatedOperator === 'ALL' && designatedOperatorOptions.length > 0) {
        setDesignatedOperator(designatedOperatorOptions[0].id);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [designatedOperator, designatedOperatorOptions, isCurrentUserSuperUser]);

    // Effect to handle SuperUser display for admin-disabled permissions
    useEffect(() => {
      // Skip entirely in add mode to avoid flicker/loops when creating users
      if (isAddMode) {
        return;
      }

      // Only apply this logic for Super Users viewing other users
      if (isCurrentUserSuperUser && user?.role !== UserRoles.SUPER_USER) {
        // Create a copy of the current permissions
        const updatedPermissions = { ...selectedPermissions };
        let hasChanges = false;
        
        // For each module with admin-disabled permissions
        Object.entries(adminDisabledPermissions).forEach(([moduleName, permissions]) => {
          if (!updatedPermissions[moduleName]) {
            updatedPermissions[moduleName] = [];
          }
          
          // Find all admin-disabled permissions
          Object.keys(permissions).forEach(permission => {
            // Add admin-disabled permissions to the selected list if not already there
            if (!updatedPermissions[moduleName].includes(permission)) {
              updatedPermissions[moduleName].push(permission);
              hasChanges = true;
            }
          });
        });
        
        // Update the permissions state if there were changes
        if (hasChanges) {
          setSelectedPermissions(updatedPermissions);
        }
      }
    }, [isAddMode, isCurrentUserSuperUser, adminDisabledPermissions, user, selectedPermissions]);

    return (
      <FormContainer data-testid="edit-user-form">
        
        {/* Validation errors alert */}
        {Object.keys(validationErrors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="validation-errors">
            <Typography variant="body2" fontWeight="bold">
              {t('profile.userManagement.validationErrors', 'Please correct the following errors:')}
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }} data-testid="validation-errors-list">
              {Object.values(validationErrors).map((error, index) => (
                <Typography component="li" variant="body2" key={index} data-testid={`validation-error-${index}`}>
                  {error}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}
        
        {/* Left and right sections container */}
        <FormSection>
          {/* Left section with form fields */}
          <LeftColumn>
            {/* Type - Moved to the top of the form */}
            <Box data-testid="user-type-section">
              <FieldLabel>
                {t('profile.userManagement.type', 'Type')}
              </FieldLabel>
              <Box sx={{ maxWidth: '360px', width: '100%' }} data-testid="user-type-control-container">
                <SegmentedControlContainer data-testid="user-type-selector">
                  <SegmentButton 
                    active={userType === AccountTypeLabels[AccountTypes.IPC_USER]} 
                    onClick={() => handleUserTypeChange(AccountTypeLabels[AccountTypes.IPC_USER])}
                    data-testid="user-type-ipc"
                    sx={{
                      opacity: !isAddMode ? 0.5 : 1, 
                      cursor: !isAddMode ? 'not-allowed' : 'pointer'
                    }}
                  >
                    IPC User
                  </SegmentButton>
                  <SegmentButton 
                    active={userType === AccountTypeLabels[AccountTypes.MAILBOX]} 
                    onClick={() => handleUserTypeChange(AccountTypeLabels[AccountTypes.MAILBOX])}
                    data-testid="user-type-mailbox"
                    sx={{
                      opacity: !isAddMode ? 0.5 : 1, 
                      cursor: !isAddMode ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Mailbox
                  </SegmentButton>
                </SegmentedControlContainer>
              </Box>
              {(userRole === UserRoleLabels[UserRoles.ADMIN] || userRole === UserRoleLabels[UserRoles.SUPER_USER]) && userType === AccountTypeLabels[AccountTypes.MAILBOX] && (
                <HelperText>
                  IPC Admin and Super User must be IPC User type
                </HelperText>
              )}
              {!isAddMode && (
                <HelperText>
                  Account type cannot be changed for existing users
                </HelperText>
              )}
            </Box>
            
            {/* SAM ID / Username (editable for add, read-only for edit) */}
            <Box data-testid="samid-section">
              <FieldLabel>
                {t('profile.userManagement.samid', 'SAM ID')}
                {isAddMode && userType === AccountTypeLabels[AccountTypes.IPC_USER] && (
                  <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                )}
              </FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={userType === AccountTypeLabels[AccountTypes.MAILBOX] ? "" : (isAddMode ? samId : username)}
                onChange={(e) => {
                  if (isAddMode && userType === AccountTypeLabels[AccountTypes.IPC_USER]) {
                    const rawValue = e.target.value;
                    
                    // Check for dangerous content
                    if (hasDangerousInput(rawValue)) {
                      setSamIdError(getInputErrorMessage(rawValue, 'SAM ID') || 'Invalid characters detected');
                      // Still sanitize and set the value, but show error
                      const sanitized = sanitizeTextInput(rawValue);
                      setSamId(sanitized);
                    } else {
                      // Validate SAM ID format
                      if (rawValue && !isValidSamId(rawValue)) {
                        setSamIdError('SAM ID can only contain letters, numbers, dots, and underscores');
                        setSamId(rawValue);
                      } else {
                        setSamIdError(null);
                        setSamId(rawValue);
                      }
                    }
                  }
                }}
                disabled={!isAddMode || userType === AccountTypeLabels[AccountTypes.MAILBOX]}
                placeholder={isAddMode && userType === AccountTypeLabels[AccountTypes.IPC_USER] ? "Enter SAM ID" : ""}
                helperText={
                  samIdError 
                    ? samIdError
                    : isAddMode 
                      ? userType === AccountTypeLabels[AccountTypes.IPC_USER] 
                        ? "Enter user's SAM ID (required for IPC User). Only letters, numbers, dots, and underscores allowed." 
                        : ""
                      : ""
                }
                required={isAddMode && userType === AccountTypeLabels[AccountTypes.IPC_USER]}
                error={(isAddMode && userType === AccountTypeLabels[AccountTypes.IPC_USER] && !samId.trim()) || !!samIdError}
                InputProps={{
                  readOnly: !isAddMode || userType === AccountTypeLabels[AccountTypes.MAILBOX],
                  sx: { 
                    backgroundColor: !isAddMode || userType === AccountTypeLabels[AccountTypes.MAILBOX] ? themeColors.light1 : 'transparent',
                    '&.Mui-disabled': {
                      color: themeColors.body,
                      opacity: 0.8,
                      WebkitTextFillColor: themeColors.body,
                    }
                  }
                }}
                data-testid="username-input"
                sx={{ 
                  maxWidth: '360px',
                  '.MuiOutlinedInput-input': { 
                    fontSize: '14px',
                    height: '20px',
                    padding: '10px 14px'
                  }
                }}
              />
              {!isAddMode && (
                <HelperText>
                  Username cannot be changed for existing users
                </HelperText>
              )}
              {isAddMode && userType === AccountTypeLabels[AccountTypes.MAILBOX] && (
                <HelperText>
                  SAM ID is disabled for Mailbox users
                </HelperText>
              )}
            </Box>

            {/* Designated Operator */}
            <Box data-testid="designated-operator-section">
              <FieldLabel>
                {t('profile.userManagement.designatedOperator', 'Designated Operator')}
              </FieldLabel>
              
              {isCurrentUserSuperUser ? (
                // For Super Users, simply show their designated operator as read-only text
                <TextField
                  fullWidth
                  size="small"
                  value={currentUserDesignatedOperator}
                  disabled
                  InputProps={{
                    readOnly: true,
                    sx: { 
                      backgroundColor: themeColors.light1,
                      '&.Mui-disabled': {
                        color: themeColors.body,
                        opacity: 0.8,
                        WebkitTextFillColor: themeColors.body,
                      }
                    }
                  }}
                  data-testid="designated-operator-readonly"
                  sx={{ 
                    maxWidth: '250px',
                    '.MuiOutlinedInput-input': { 
                      fontSize: '14px',
                      height: '20px',
                      padding: '10px 14px'
                    }
                  }}
                />
              ) : (
                // For Admins, show the full dropdown with search
                <FormControl fullWidth size="small" sx={{ maxWidth: '250px' }}>
                  <Select
                    value={designatedOperator}
                    onChange={(e) => setDesignatedOperator(e.target.value)}
                    displayEmpty
                    IconComponent={KeyboardArrowDownIcon}
                    disabled={!canEditDesignatedOperator || isLoadingOperators}
                    data-testid="designated-operator-select"
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }}
                    sx={{ 
                      height: '40px',
                      fontSize: '14px',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: designatedOperatorsError ? '#d32f2f' : themeColors.body,
                        borderRadius: '4px'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: themeColors.light1,
                        '.MuiOutlinedInput-input': {
                          color: themeColors.body,
                          opacity: 0.8,
                          WebkitTextFillColor: themeColors.body,
                        }
                      }
                    }}
                    renderValue={(selected) => {
                      if (isLoadingOperators) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={18} sx={{ mr: 1 }} />
                            Loading...
                          </Box>
                        );
                      }
                      
                      if (designatedOperatorsError) {
                        return "Error loading operators";
                      }
                      
                      // Find the selected operator in the options
                      const selectedOption = designatedOperatorOptions.find(option => option.id === selected);
                      return selectedOption?.label || selected;
                    }}
                  >
                    {isLoadingOperators ? (
                      <MenuItem disabled>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={18} sx={{ mr: 1 }} />
                          Loading...
                        </Box>
                      </MenuItem>
                    ) : designatedOperatorsError ? (
                      <MenuItem disabled>
                        <Box sx={{ color: '#d32f2f' }}>
                          Failed to load operators
                        </Box>
                      </MenuItem>
                    ) : designatedOperatorOptions.length === 0 ? (
                      <MenuItem disabled>
                        <Box sx={{ fontStyle: 'italic', color: themeColors.midGrey }}>
                          No designated operators available
                        </Box>
                      </MenuItem>
                    ) : (
                      designatedOperatorOptions.map((option) => (
                        <MenuItem 
                          key={option.id} 
                          value={option.id} 
                          data-testid={`designated-operator-option-${option.id.toLowerCase()}`}
                        >
                          {option.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {isLoadingOperators && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Loading designated operators...
                    </Typography>
                  )}
                  {designatedOperatorsError && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      Failed to load designated operators. Please try again.
                    </Typography>
                  )}
                </FormControl>
              )}
              
              {isCurrentUserSuperUser && (
                <HelperText>
                  Super Users can only use their own designated operator
                </HelperText>
              )}
            </Box>

            {/* Email */}
            <Box data-testid="email-section">
              <FieldLabel>
                {t('profile.userManagement.email', 'Email Address')}
                {userType === AccountTypeLabels[AccountTypes.MAILBOX] && (
                  <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                )}
              </FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={userType === AccountTypeLabels[AccountTypes.IPC_USER]}
                required={userType === AccountTypeLabels[AccountTypes.MAILBOX]}
                error={userType === AccountTypeLabels[AccountTypes.MAILBOX] && 
                  (email.trim() === '' || (email.trim() !== '' && !isValidEmail(email)))}
                helperText={
                  userType === AccountTypeLabels[AccountTypes.MAILBOX] && 
                  (email.trim() === '' ? 
                    t('profile.userManagement.emailRequired', 'Email is required for Mailbox users') : 
                    !isValidEmail(email) && email.trim() !== '' ? 
                    t('profile.userManagement.invalidEmail', 'Please enter a valid email address') : 
                    '')
                }
                InputProps={{
                  readOnly: userType === AccountTypeLabels[AccountTypes.IPC_USER],
                  sx: { 
                    backgroundColor: userType === AccountTypeLabels[AccountTypes.IPC_USER] ? themeColors.light1 : 'transparent',
                    '&.Mui-disabled': {
                      color: themeColors.body,
                      opacity: 0.8,
                      WebkitTextFillColor: themeColors.body,
                    }
                  }
                }}
                data-testid="email-input"
                sx={{ 
                  maxWidth: '360px',
                  '.MuiOutlinedInput-input': { 
                    fontSize: '14px',
                    height: '20px',
                    padding: '10px 14px'
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: themeColors.body,
                    borderRadius: '4px'
                  }
                }}
              />
              {userType === AccountTypeLabels[AccountTypes.IPC_USER] && (
                <HelperText>
                  Email cannot be changed for IPC Users
                </HelperText>
              )}
            </Box>

            {/* User Role */}
            <Box data-testid="user-role-section">
              <FieldLabel>
                {t('profile.userManagement.userType', 'User Role')}
              </FieldLabel>
              <Box sx={{ maxWidth: '360px', width: '100%' }} data-testid="user-role-control-container">
                <SegmentedControlContainer data-testid="user-role-selector">
                  {!isCurrentUserSuperUser && (
                  <SegmentButton 
                    active={userRole === UserRoleLabels[UserRoles.ADMIN]} 
                    onClick={() => handleUserRoleChange(UserRoleLabels[UserRoles.ADMIN])}
                    data-testid="user-role-admin"
                    sx={{
                      opacity: isCurrentUserAdmin ? 1 : 0.5,
                      cursor: isCurrentUserAdmin ? 'pointer' : 'not-allowed'
                    }}
                  >
                    IPC Admin
                  </SegmentButton>
                  )}
                  <SegmentButton 
                    active={userRole === UserRoleLabels[UserRoles.SUPER_USER]} 
                    onClick={() => handleUserRoleChange(UserRoleLabels[UserRoles.SUPER_USER])}
                    data-testid="user-role-super"
                    
                  >
                    Super User
                  </SegmentButton>
                  <SegmentButton 
                    active={userRole === UserRoleLabels[UserRoles.USER]} 
                    onClick={() => handleUserRoleChange(UserRoleLabels[UserRoles.USER])}
                    data-testid="user-role-user"
                  >
                    User
                  </SegmentButton>
                </SegmentedControlContainer>
              </Box>
              {userRole === UserRoleLabels[UserRoles.SUPER_USER] && (
                <HelperText>
                  Super User modules and permissions are enabled by default but can be customized
                </HelperText>
              )}
            </Box>
          </LeftColumn>

          {/* Right section with module toggles */}
          <RightColumn data-testid="module-toggles-container">
            {/* Display message for Admin or Super User roles */} 
            {/* Add explanation for admin-restricted modules/permissions */}
            {!isCurrentUserAdmin && Object.keys(adminDisabledModules).length > 0 && (
              <HelperText data-testid="admin-restricted-message" sx={{ mb: 1, color: '#d32f2f' }}>
                {t('profile.userManagement.adminRestrictedInfo', 'Items marked as "Admin restricted" can only be modified by Admin')}
              </HelperText>
            )}
            
            {/* Show message for Super Users in add mode */}
            {isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin && (
              <HelperText data-testid="super-user-restricted-message" sx={{ mb: 1, color: '#d32f2f' }}>
                {t('profile.userManagement.superUserRestrictedInfo', 'You can only grant permissions and modules that you have access to. Admin-restricted items cannot be granted.')}
              </HelperText>
            )}
            
            {/* Add safety check if moduleToggles is empty */}
            {Object.keys(moduleToggles).length === 0 ? (
              <Typography sx={{ color: themeColors.midGrey, fontSize: '14px' }}>
                No modules available to configure
              </Typography>
            ) : (
              Object.entries(moduleToggles).map(([module, isChecked]) => {
                // Check if this module is admin-disabled
                const isAdminDisabled = adminDisabledModules[module] || false;
                
                // Check if Super User doesn't have access to this module (in add mode)
                let isDisabledByPermission = false;
                if (isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin) {
                  // Check if current user has this module admin-disabled
                  const currentUserHasAdminDisabled = currentUserAdminDisabledModules[module] === true;
                  // Check if current user has this module enabled (not admin-disabled)
                  const currentUserHasAccess = currentUserEnabledModules[module] === true;
                  // Module is disabled if it's admin-disabled OR if user doesn't have access
                  isDisabledByPermission = currentUserHasAdminDisabled || !currentUserHasAccess;
                }
                
                // Disable the toggle if it's admin-disabled or if Super User doesn't have access
                const isDisabled = (isAdminDisabled && !isCurrentUserAdmin) || isDisabledByPermission;
                
                // Show "Admin restricted" label if module is disabled for any reason
                const shouldShowAdminRestricted = isAdminDisabled || isDisabledByPermission;
                
                return (
                  <ModuleToggleContainer key={module} data-testid={`module-toggle-${module.toLowerCase()}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid={`module-label-container-${module.toLowerCase()}`}>
                      <ModuleToggleLabel>{module.replace('_', ' ')}</ModuleToggleLabel>
                      {shouldShowAdminRestricted && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 11,
                            color: '#d32f2f',
                            ml: 1,
                            backgroundColor: 'rgba(211, 47, 47, 0.05)',
                            padding: '0px 4px',
                            borderRadius: '2px',
                          }}
                        >
                          Admin restricted
                        </Typography>
                      )}
                    </Box>
                    <CustomSwitch 
                      checked={isChecked} 
                      onChange={handleModuleToggle(module)}
                      disabled={isDisabled}
                      data-testid={`module-switch-${module.toLowerCase()}`}
                    />
                  </ModuleToggleContainer>
                );
              })
            )}
          </RightColumn>
        </FormSection>

        <Divider sx={{ my: 1, backgroundColor: themeColors.light2 }} />

        {/* Module permissions */}
        <PermissionsContainer data-testid="module-permissions-container">
          {Object.entries(modulePermissions).map(([moduleName, permissions]) => {
            // Find which toggle controls this permission section
            const toggleKey = Object.entries(moduleToPermissionMap).find(
              ([_, value]) => value === moduleName
            )?.[0];
            
            // For all users (including Super Users), only show modules that are toggled on
            const shouldShowModule = toggleKey && moduleToggles[toggleKey];
            
            // Skip modules that shouldn't be shown
            if (!shouldShowModule) {
              return null;
            }
            
            return (
              <ModuleSection key={moduleName} data-testid={`module-section-${moduleName.toLowerCase().replace(/\s+/g, '-')}`}>
                <ModuleSectionHeader>
                  <ModuleSectionTitle>
                    {moduleName}
                  </ModuleSectionTitle>
                  <SelectAllButton 
                    onClick={handleSelectAll(moduleName)}
                    data-testid={`select-all-${moduleName.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    Select all
                  </SelectAllButton>
                </ModuleSectionHeader>
                <CheckboxList data-testid={`checkbox-list-${moduleName.toLowerCase().replace(/\s+/g, '-')}`}>
                  {permissions.map((permission) => {
                    // Check if this permission is admin-disabled
                    const isAdminDisabled = adminDisabledPermissions[moduleName]?.[permission] || false;
                    
                    // Check if Super User doesn't have access to this permission (in add mode)
                    let isDisabledByPermission = false;
                    if (isAddMode && isCurrentUserSuperUser && !isCurrentUserAdmin) {
                      // Check if current user has this permission admin-disabled
                      const currentUserHasAdminDisabled = currentUserAdminDisabledPermissions[moduleName]?.[permission] === true;
                      // Check if current user has access to this permission
                      const currentUserHasPermission = currentUserAccessiblePermissions[moduleName]?.includes(permission);
                      // Permission is disabled if it's admin-disabled OR if user doesn't have access
                      isDisabledByPermission = currentUserHasAdminDisabled || !currentUserHasPermission;
                    }
                    
                    // Disable the checkbox if it's admin-disabled or if Super User doesn't have access
                    const isDisabled = (isAdminDisabled && !isCurrentUserAdmin) || isDisabledByPermission;
                    
                    // Determine if the checkbox should be checked based on the current selections
                    const isChecked = selectedPermissions[moduleName]?.includes(permission) || false;
                    
                    return (
                      <CustomCheckbox
                        key={permission}
                        checked={isChecked}
                        onChange={handlePermissionChange(moduleName, permission)}
                        label={permission}
                        disabled={isDisabled}
                        isAdminDisabled={isAdminDisabled || isDisabledByPermission}
                        data-testid={`permission-checkbox-${moduleName.toLowerCase().replace(/\s+/g, '-')}-${permission.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                    );
                  })}
                </CheckboxList>
              </ModuleSection>
            );
          }).filter(Boolean)}
        </PermissionsContainer>
      </FormContainer>
    );
  }
);

export default EditUser; 